/**
 * OrgChangeConfirm — Step 3 of OrgChangeStepper (v0.14 Wave 3)
 *
 * Cascade preview + Apply. Per spec:
 *   §1.0   — confirmation panel with named recipient list + address preview
 *   §1.9   — NULL-DBA dominant case (83% of prod): suppress DBA-cleared line
 *   §2.3   — Address resolver preview matrix (5 source values)
 *   §11.3  — 0-recipients omit the whole block (87% dominant case)
 *   §13    — Recipient cascade copy "FROM THIS INVOICE ONLY" + scope tags
 *   §14.7  — Source-DBA archive choice on create-standalone path only
 *
 * Apply button = single click, no Layer-B friction (per locked decision +
 * Jason's #3). Copy makes destructive scope explicit instead.
 *
 * Vocabulary lock per §0.2:
 *   - "Client" not "organization"
 *   - "Billing track" not "scope"
 *   - "DBA" not "establishment"
 */
import type { ReactNode } from 'react';
import { Loader2, ChevronLeft, AlertCircle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { OrgChangeStepperInvoice, OrgChangeRecipientPreview } from './OrgChangeStepper';

export interface OrgChangeConfirmProps {
  invoice: OrgChangeStepperInvoice;
  targetClient: { id: string; name: string };
  /** Christa-friction-polish F-5 (2026-05-30): target client's
   *  `billing_address` pre-fetched by the stepper via useOrganization. Used by
   *  the address preview when the invoice's billing_address_source is
   *  'organization' — without this, the preview lied that the address would
   *  be blank when in practice the target client often has one on file.
   *  Pass null when the target has no billing_address on file (the existing
   *  blank+warning state is the truth then). */
  targetClientBillingAddress?: string | null;
  recipientsToBeCleared: OrgChangeRecipientPreview[];
  /** §5.13 carry-through: when set, DBA on the new invoice will be set to
   *  this DBA instead of cleared. */
  carryThroughDBA: { establishmentId: number; establishmentName: string } | null;
  /** True when create-standalone path (§14.7 archive choice surfaced). */
  isCreateStandalonePath: boolean;
  sourceDbaName: string | null;
  sourceDbaParentName: string | null;
  archiveSourceDba: boolean;
  onArchiveChange: (v: boolean) => void;
  allowArchiveChoice?: boolean;
  /** §1.2 + R8e Christa-UX #3 (fixwave 2026-05-28): when set, Step 3
   *  renders the named smart-default billing track. When null/undefined,
   *  falls back to generic "create or attach" copy.
   *
   *  Shape mirrors the backend's `find_client_scope_for_smart_default`
   *  result that will be exposed via a pre-flight GET (or returned by
   *  Wave 3's enhanced search-clients endpoint). Frontend is forward-
   *  compatible: passes through whatever shape lands. */
  scopeAction?: {
    action: 'found' | 'will_create';
    establishment_name?: string | null;
    last_invoiced_date?: string | null;
    invoice_count_90d?: number | null;
  } | null;
  onApply: () => void;
  onBack: () => void;
  isApplying: boolean;
}

/** Resolve the human-readable address that will print on the PDF post-switch.
 *  Mirrors backend resolver behavior per §2.3 matrix — for preview only;
 *  authoritative resolution happens server-side on Apply.
 *
 *  R8e Christa-UX #2 fix (fixwave 2026-05-28): handles all 5 source values
 *  from §2.3 (project, establishment, organization, custom, NULL) — the
 *  organization branch was previously missing and fell through to the
 *  generic "Address source not set" path (factually wrong for the 14% of
 *  invoices that have an explicit organization source).
 */
function resolveAddressPreview(
  invoice: OrgChangeStepperInvoice,
  targetClientName?: string,
  targetClientBillingAddress?: string | null,
): {
  value: string | null;
  sourceLabel: string;
  warning: boolean;
  /** UX6 (v0.14 polish): "unchanged" → keep using the same source post-switch;
   *  "changed" → source flips (e.g. establishment → project on org-change);
   *  "preserved" → custom override carries over verbatim. Distinguishes how
   *  the chip renders so Christa sees source-changes shouting and source-
   *  unchanged quiet. */
  changeKind: 'unchanged' | 'changed' | 'preserved';
} {
  const source = invoice.billing_address_source ?? null;

  // custom: preserved verbatim
  if (source === 'custom') {
    return {
      value: invoice.billing_address_override ?? '',
      sourceLabel: 'Custom address on this invoice — preserved',
      warning: false,
      changeKind: 'preserved',
    };
  }
  // establishment: auto-flips to project on org change (DBA cleared)
  if (source === 'establishment') {
    if (invoice.project_address) {
      return {
        value: invoice.project_address,
        sourceLabel: `Will switch to use ${invoice.project_name || 'project'}'s billing address`,
        warning: false,
        changeKind: 'changed',
      };
    }
    return {
      value: null,
      sourceLabel: `No address set on ${invoice.project_name || 'this project'}`,
      warning: true,
      changeKind: 'changed',
    };
  }
  // project: unchanged
  if (source === 'project') {
    return {
      value: invoice.project_address ?? '',
      sourceLabel: `Will continue to use ${invoice.project_name || 'project'}'s billing address`,
      warning: !invoice.project_address,
      changeKind: 'unchanged',
    };
  }
  // organization: re-resolves under the NEW client per §2.3.
  // Christa-friction-polish F-5 (2026-05-30): when the stepper has pre-
  // fetched the target client's billing_address, render the actual resolved
  // value here so Christa sees the truthful post-switch state instead of an
  // amber "address will be blank" warning that often lied. When the target
  // really has no billing_address on file, keep the existing warning — that's
  // the truthful "you'll need to set this after" path.
  if (source === 'organization') {
    const clientLabel = targetClientName || 'the new client';
    if (targetClientBillingAddress && targetClientBillingAddress.trim() !== '') {
      return {
        value: targetClientBillingAddress,
        sourceLabel: `Will use ${clientLabel}'s billing address`,
        warning: false,
        changeKind: 'changed',
      };
    }
    return {
      value: null,
      sourceLabel: `Will use ${clientLabel}'s billing address (resolved on save)`,
      warning: true,
      changeKind: 'changed',
    };
  }
  // NULL/unset → falls back to project address per existing v0.13 resolver
  return {
    value: invoice.project_address ?? '',
    sourceLabel: 'Address source not set — will use project default',
    warning: !invoice.project_address,
    changeKind: 'changed',
  };
}

export function OrgChangeConfirm({
  invoice,
  targetClient,
  targetClientBillingAddress,
  recipientsToBeCleared,
  carryThroughDBA,
  isCreateStandalonePath,
  sourceDbaName,
  sourceDbaParentName,
  archiveSourceDba,
  onArchiveChange,
  allowArchiveChoice = true,
  scopeAction,
  onApply,
  onBack,
  isApplying,
}: OrgChangeConfirmProps) {
  const oldClientName = invoice.client_name || 'the current client';
  const isSwitchingFromNothing = !invoice.organization_id;
  const recipientCount = recipientsToBeCleared.length;

  // §1.9: NULL-DBA dominant case. Suppress the DBA-cleared block when there
  // was nothing to clear (no establishment_id on the invoice).
  const hadDba = !!invoice.establishment_id;
  const showDbaSection = hadDba || !!carryThroughDBA;

  // §11.3: 0-recipients dominant case. Omit the entire recipients block.
  const showRecipientsSection = recipientCount > 0;

  const addressPreview = resolveAddressPreview(
    invoice,
    targetClient.name,
    targetClientBillingAddress,
  );

  return (
    <div className="space-y-4" data-testid="org-change-confirm">
      {/* Visual switching heading. §1.7: copy adapts to "set" vs "switch". */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
        <p className="text-sm text-slate-600">
          {isSwitchingFromNothing ? (
            <>Will set the client on this invoice to:</>
          ) : (
            <>
              Will switch this invoice from{' '}
              <span className="font-semibold text-slate-900">{oldClientName}</span>{' '}
              to:
            </>
          )}
        </p>
        <p
          className="text-base font-semibold text-slate-900 mt-1"
          data-testid="org-change-confirm-target-name"
        >
          {targetClient.name}
        </p>
      </div>

      {/* Cascade preview list */}
      <ul className="space-y-2 text-sm">
        {/* DBA section — §1.9 dominant case suppression */}
        {showDbaSection && (
          <li
            className="flex items-start gap-2"
            data-testid="org-change-confirm-dba-line"
          >
            <Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              {carryThroughDBA ? (
                <span>
                  <span className="font-medium">DBA</span> will be set to{' '}
                  <span className="font-semibold">
                    {carryThroughDBA.establishmentName}
                  </span>
                  .
                </span>
              ) : hadDba ? (
                <span>
                  Current DBA{' '}
                  <span className="font-semibold">{invoice.establishment_name}</span>{' '}
                  will be cleared &mdash; pick a new DBA under {targetClient.name}{' '}
                  after switching if needed.
                </span>
              ) : null}
            </div>
          </li>
        )}

        {/* Recipients section — §11.3 + §0.2 empty-state principle: omit when 0 */}
        {showRecipientsSection && (
          <li
            className="flex items-start gap-2"
            data-testid="org-change-confirm-recipients-block"
          >
            <Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p>
                Recipients on this invoice will be removed{' '}
                <span className="font-semibold">(FROM THIS INVOICE ONLY)</span>
                {invoice.organization_id ? (
                  <>
                    {' '}
                    &mdash; they stay attached to the{' '}
                    <span className="font-medium">{oldClientName}</span> record:
                  </>
                ) : (
                  ':'
                )}
              </p>
              <ul
                className="mt-1 space-y-0.5 pl-4"
                data-testid="org-change-confirm-recipients-list"
              >
                {recipientsToBeCleared.map((r, idx) => (
                  <li
                    key={`${r.email}-${idx}`}
                    className="text-xs text-slate-600"
                    data-testid={`org-change-confirm-recipient-${idx}`}
                  >
                    <span className="font-medium text-slate-800">
                      {r.name || '(unnamed)'}
                    </span>
                    {r.email && <span className="text-slate-500"> &lt;{r.email}&gt;</span>}
                    {r.scopeTag && (
                      <span className="text-slate-500"> &mdash; {r.scopeTag}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </li>
        )}

        {/* Address preview — always shown (§2.3 matrix always renders a row).
            UX6 (v0.14 polish): the source label was previously rendered as a
            small grey parenthetical inline with the heading
            ("Address that will print: (Will continue to use ...)") — the
            Playwright review flagged that as too easy to miss when the source
            actually CHANGES (e.g. establishment → project on org-change).
            Replaced with a coloured chip/badge so source-changes are visually
            distinct from source-unchanged and match the rest of the BILL TO
            panel hierarchy (cf. the "Hidden on PDF" pill on the DBA row). */}
        <li
          className="flex items-start gap-2"
          data-testid="org-change-confirm-address-line"
        >
          <Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">Address that will print:</span>
              <span
                className={
                  // chip variants:
                  //   unchanged → muted slate (quiet, "nothing to look at")
                  //   changed   → amber (source flip — calls attention)
                  //   preserved → blue (custom-text passthrough)
                  addressPreview.changeKind === 'changed'
                    ? 'inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded'
                    : addressPreview.changeKind === 'preserved'
                    ? 'inline-flex items-center gap-1 text-xs font-medium text-blue-800 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded'
                    : 'inline-flex items-center gap-1 text-xs font-medium text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded'
                }
                data-testid="org-change-confirm-address-source-chip"
                data-change-kind={addressPreview.changeKind}
              >
                {addressPreview.sourceLabel}
              </span>
            </div>
            {addressPreview.value ? (
              <p className="text-xs text-slate-700 mt-1 whitespace-pre-line bg-slate-50 px-2 py-1 rounded border border-slate-200">
                {addressPreview.value}
              </p>
            ) : (
              <p className="text-xs text-amber-700 mt-1 inline-flex items-start gap-1">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Address will be blank &mdash; please set an address on this
                  invoice after switching.
                </span>
              </p>
            )}
          </div>
        </li>

        {/* Billing track — §1.2 + R8e Christa-UX #3 (fixwave 2026-05-28).
            When scopeAction is provided (Wave 2 backend pre-flight), name
            the resolved track. Otherwise fall back to generic copy.

            fixwave iter3 (2026-05-28): R8f-3 #N1 — previously the 'found'
            branch was gated on `establishment_name` being truthy, so any
            existing scope with a NULL DBA fell through to the generic
            "will either create OR attach" copy (the playwright-observed
            failure on Riverside Client 51 + V014W2Arch7002425 Parent).
            Spec §1.2 wants Christa to see the named-track reassurance
            whenever a scope exists, regardless of whether the scope is
            DBA-attached or org-level. We now branch on `action === 'found'`
            alone and degrade the in-parens detail string across 4
            sub-cases (per R8f-3 recommendation). */}
        <li
          className="flex items-start gap-2"
          data-testid="org-change-confirm-billing-track-line"
        >
          <Check className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            {scopeAction?.action === 'found' ? (
              (() => {
                const {
                  establishment_name,
                  last_invoiced_date,
                  invoice_count_90d,
                } = scopeAction;
                // Build the parenthetical detail. Drops parts when missing
                // (DBA-less scope, never-invoiced scope) and omits the
                // parens entirely when neither is known.
                const detailParts: ReactNode[] = [];
                if (establishment_name) {
                  detailParts.push(
                    <span className="font-medium" key="est">
                      {establishment_name}
                    </span>,
                  );
                }
                if (last_invoiced_date) {
                  detailParts.push(
                    <span key="date">last invoiced {last_invoiced_date}</span>,
                  );
                }
                if (
                  typeof invoice_count_90d === 'number' &&
                  invoice_count_90d > 0
                ) {
                  detailParts.push(
                    <span key="count">
                      {invoice_count_90d} invoice
                      {invoice_count_90d === 1 ? '' : 's'} in past 90 days
                    </span>,
                  );
                }
                return (
                  <p>
                    <span className="font-medium">Billing track:</span> will use{' '}
                    {targetClient.name}&apos;s existing track
                    {detailParts.length > 0 && (
                      <>
                        {' '}
                        (
                        {detailParts.map((part, idx) => (
                          <span key={idx}>
                            {part}
                            {idx < detailParts.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                        )
                      </>
                    )}{' '}
                    on {invoice.project_name || 'this project'}.
                  </p>
                );
              })()
            ) : scopeAction?.action === 'will_create' ? (
              <p>
                <span className="font-medium">Billing track:</span> will create a
                new track on{' '}
                <span className="font-medium">
                  {invoice.project_name || 'this project'}
                </span>{' '}
                for {targetClient.name}.
              </p>
            ) : (
              <p>
                <span className="font-medium">Billing track:</span> will either
                create a new billing track on{' '}
                <span className="font-medium">
                  {invoice.project_name || 'this project'}
                </span>{' '}
                for {targetClient.name}, or attach to {targetClient.name}&apos;s
                existing track on this project if one exists.
              </p>
            )}
          </div>
        </li>
      </ul>

      {/* §14.7: Source-DBA archive choice (only on create-standalone path) */}
      {isCreateStandalonePath && sourceDbaName && sourceDbaParentName && allowArchiveChoice && (
        <div
          className="border border-slate-200 rounded-md p-3 bg-amber-50/50"
          data-testid="org-change-confirm-archive-block"
        >
          <p className="text-sm font-medium text-slate-900 mb-2">
            There&apos;s already a DBA &quot;{sourceDbaName}&quot; under{' '}
            {sourceDbaParentName}.
          </p>
          <p className="text-xs text-slate-600 mb-3">
            After creating {sourceDbaName} as a standalone client, do you want
            to archive that DBA?
          </p>
          <div className="space-y-1.5">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="org-change-archive"
                checked={archiveSourceDba === true}
                onChange={() => onArchiveChange(true)}
                className="mt-1"
                data-testid="org-change-confirm-archive-yes"
              />
              <span className="text-sm">
                <span className="font-medium">Yes, archive</span> &mdash; keeps
                the system clean{' '}
                <span className="text-xs text-slate-500">(recommended)</span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="org-change-archive"
                checked={archiveSourceDba === false}
                onChange={() => onArchiveChange(false)}
                className="mt-1"
                data-testid="org-change-confirm-archive-no"
              />
              <span className="text-sm">
                <span className="font-medium">No, keep both</span> &mdash; they
                may be legitimately different
              </span>
            </label>
          </div>
          {/* Christa-friction-polish DEF-3 (2026-05-30): Archived DBAs have
              no in-app un-archive surface in v0.14 (deferred to v0.15). Name
              the limit + give Christa a "tell Jason" escape so an accidental
              archive doesn't read as a dead end. Slate caption — no chip, no
              amber. */}
          <p
            className="mt-3 text-xs text-slate-500 italic leading-relaxed"
            data-testid="org-change-confirm-archive-hint"
          >
            Archived DBAs are hidden from pickers, but historical invoices
            still show them. Restoring an archived DBA isn't available yet.
          </p>
        </div>
      )}
      {isCreateStandalonePath && !allowArchiveChoice && (
        <p className="rounded border border-slate-200 p-3 text-sm text-slate-700">
          The original DBA record will remain linked to existing invoices. This client change will not archive it.
        </p>
      )}

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          data-testid="org-change-confirm-back"
          disabled={isApplying}
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
        <Button
          onClick={onApply}
          disabled={isApplying}
          data-testid="org-change-confirm-apply"
        >
          {isApplying ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Opening review...
            </>
          ) : (
            'Review client change'
          )}
        </Button>
      </div>
    </div>
  );
}
